import { CountDownBarProps } from '../components/countDownBar';
import chamEventDaysConfig from '../data/ChamEventDays.json';
import sakawiTakaiCiimConfig from '../data/SakawiTakaiCiim.json';
import { AhierMonthEnum, AreaType, AwalMonthEnum, EventType, GuecTypeEnum, GuenTypeEnum, IkasSarakEnum, NasakEnum } from "../enums/enum";
import { AhierDate, AhierMonth, AhierYear } from "../model/AhierDate";
import { AwalDate, AwalMonth, AwalYear } from '../model/AwalDate';
import { FullCalendarType } from '../model/FullCalendarType';
import { MatrixCalendarType } from "../model/MatrixCalendarType";
import { getToday } from '../utils/today';
import { awalMonthArray, awalYearArray, firstDateOfSakawiAhier_InaGirai_Lieh_1988, firstDateOfSakawiAwal_Lieh_1407, totalDaysOf8AwalYearCycle, yearNumberOfSakawiAwal_Lieh_1407 } from './constant';

export default class Helper {
    static isVietnameseLunarNewYear(date: Date) {
        const lunarDate = Helper.convertSolarToVietnameseLunarDate(date);
        return lunarDate.day === 1 && lunarDate.month === 1;
    }

    private static convertSolarToVietnameseLunarDate(date: Date) {
        const timeZone = 7;
        const dayNumber = Helper.getJulianDayNumber(date.getDate(), date.getMonth() + 1, date.getFullYear());
        const k = Math.floor((dayNumber - 2415021.076998695) / 29.530588853);
        let monthStart = Helper.getNewMoonDay(k + 1, timeZone);

        if (monthStart > dayNumber) {
            monthStart = Helper.getNewMoonDay(k, timeZone);
        }

        let lunarYear: number;
        let lunarMonth: number;
        const lunarDay = dayNumber - monthStart + 1;
        let lunarMonth11 = Helper.getLunarMonth11(date.getFullYear(), timeZone);
        let previousLunarMonth11 = lunarMonth11;

        if (lunarMonth11 >= monthStart) {
            lunarYear = date.getFullYear();
            lunarMonth11 = Helper.getLunarMonth11(date.getFullYear() - 1, timeZone);
        } else {
            lunarYear = date.getFullYear() + 1;
            previousLunarMonth11 = Helper.getLunarMonth11(date.getFullYear() + 1, timeZone);
        }

        const diff = Math.floor((monthStart - lunarMonth11) / 29);
        lunarMonth = diff + 11;

        if (previousLunarMonth11 - lunarMonth11 > 365) {
            const leapMonthDiff = Helper.getLeapMonthOffset(lunarMonth11, timeZone);

            if (diff >= leapMonthDiff) {
                lunarMonth = diff + 10;
            }
        }

        if (lunarMonth > 12) {
            lunarMonth -= 12;
        }

        if (lunarMonth >= 11 && diff < 4) {
            lunarYear -= 1;
        }

        return {
            day: lunarDay,
            month: lunarMonth,
            year: lunarYear
        };
    }

    private static getJulianDayNumber(day: number, month: number, year: number) {
        const a = Math.floor((14 - month) / 12);
        const y = year + 4800 - a;
        const m = month + 12 * a - 3;

        return day + Math.floor((153 * m + 2) / 5) + 365 * y + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400) - 32045;
    }

    private static getNewMoonDay(k: number, timeZone: number) {
        const timeInJulianCenturies = k / 1236.85;
        const timeSquared = timeInJulianCenturies * timeInJulianCenturies;
        const timeCubed = timeSquared * timeInJulianCenturies;
        const dr = Math.PI / 180;
        let julianDay = 2415020.75933 + 29.53058868 * k + 0.0001178 * timeSquared - 0.000000155 * timeCubed;
        julianDay += 0.00033 * Math.sin((166.56 + 132.87 * timeInJulianCenturies - 0.009173 * timeSquared) * dr);

        const meanNewMoon = 359.2242 + 29.10535608 * k - 0.0000333 * timeSquared - 0.00000347 * timeCubed;
        const sunMeanAnomaly = 306.0253 + 385.81691806 * k + 0.0107306 * timeSquared + 0.00001236 * timeCubed;
        const moonArgumentOfLatitude = 21.2964 + 390.67050646 * k - 0.0016528 * timeSquared - 0.00000239 * timeCubed;

        let correction = (0.1734 - 0.000393 * timeInJulianCenturies) * Math.sin(meanNewMoon * dr);
        correction += 0.0021 * Math.sin(2 * meanNewMoon * dr);
        correction -= 0.4068 * Math.sin(sunMeanAnomaly * dr);
        correction += 0.0161 * Math.sin(2 * sunMeanAnomaly * dr);
        correction -= 0.0004 * Math.sin(3 * sunMeanAnomaly * dr);
        correction += 0.0104 * Math.sin(2 * moonArgumentOfLatitude * dr);
        correction -= 0.0051 * Math.sin((meanNewMoon + sunMeanAnomaly) * dr);
        correction -= 0.0074 * Math.sin((meanNewMoon - sunMeanAnomaly) * dr);
        correction += 0.0004 * Math.sin((2 * moonArgumentOfLatitude + meanNewMoon) * dr);
        correction -= 0.0004 * Math.sin((2 * moonArgumentOfLatitude - meanNewMoon) * dr);
        correction -= 0.0006 * Math.sin((2 * moonArgumentOfLatitude + sunMeanAnomaly) * dr);
        correction += 0.0010 * Math.sin((2 * moonArgumentOfLatitude - sunMeanAnomaly) * dr);
        correction += 0.0005 * Math.sin((2 * sunMeanAnomaly + meanNewMoon) * dr);

        let deltaT: number;
        if (timeInJulianCenturies < -11) {
            deltaT = 0.001 + 0.000839 * timeInJulianCenturies + 0.0002261 * timeSquared - 0.00000845 * timeCubed - 0.000000081 * timeInJulianCenturies * timeCubed;
        } else {
            deltaT = -0.000278 + 0.000265 * timeInJulianCenturies + 0.000262 * timeSquared;
        }

        return Math.floor(julianDay + correction - deltaT + 0.5 + timeZone / 24);
    }

    private static getSunLongitude(julianDayNumber: number, timeZone: number) {
        const timeInJulianCenturies = (julianDayNumber - 2451545.5 - timeZone / 24) / 36525;
        const timeSquared = timeInJulianCenturies * timeInJulianCenturies;
        const dr = Math.PI / 180;
        const meanAnomaly = 357.52910 + 35999.05030 * timeInJulianCenturies - 0.0001559 * timeSquared - 0.00000048 * timeSquared * timeInJulianCenturies;
        const meanLongitude = 280.46645 + 36000.76983 * timeInJulianCenturies + 0.0003032 * timeSquared;
        let longitude = meanLongitude;
        longitude += (1.914600 - 0.004817 * timeInJulianCenturies - 0.000014 * timeSquared) * Math.sin(dr * meanAnomaly);
        longitude += (0.019993 - 0.000101 * timeInJulianCenturies) * Math.sin(2 * dr * meanAnomaly);
        longitude += 0.000290 * Math.sin(3 * dr * meanAnomaly);
        longitude = longitude * dr;
        longitude = longitude - Math.PI * 2 * Math.floor(longitude / (Math.PI * 2));

        return Math.floor(longitude / Math.PI * 6);
    }

    private static getLunarMonth11(year: number, timeZone: number) {
        const off = Helper.getJulianDayNumber(31, 12, year) - 2415021;
        const k = Math.floor(off / 29.530588853);
        let newMoon = Helper.getNewMoonDay(k, timeZone);
        const sunLongitude = Helper.getSunLongitude(newMoon, timeZone);

        if (sunLongitude >= 9) {
            newMoon = Helper.getNewMoonDay(k - 1, timeZone);
        }

        return newMoon;
    }

    private static getLeapMonthOffset(lunarMonth11: number, timeZone: number) {
        const k = Math.floor((lunarMonth11 - 2415021.076998695) / 29.530588853 + 0.5);
        let lastSunLongitude = 0;
        let i = 1;
        let sunLongitude = Helper.getSunLongitude(Helper.getNewMoonDay(k + i, timeZone), timeZone);

        do {
            lastSunLongitude = sunLongitude;
            i++;
            sunLongitude = Helper.getSunLongitude(Helper.getNewMoonDay(k + i, timeZone), timeZone);
        } while (sunLongitude !== lastSunLongitude && i < 14);

        return i - 1;
    }

    //#region Awal
    static addAwalDays(currentDate: AwalDate, addedDays: number) {
        let numberOfDays = Helper.getDayNumbersOfAwalMonth(currentDate.awalMonth.year, currentDate.awalMonth.month);
        let newDays = currentDate.date + addedDays;
        let result: AwalDate = {
            date: 1,
            awalMonth: {
                month: AwalMonthEnum.Jamadilakhir,
                year: { ikasSarak: IkasSarakEnum.Liéh }
            }
        };

        if (newDays > numberOfDays) {
            const nextMonth = Helper.addAwalMonths(currentDate.awalMonth, 1);
            result = {
                date: newDays - numberOfDays,
                awalMonth: nextMonth
            };

        } else if (newDays <= 0) {
            const previousMonth = Helper.addAwalMonths(currentDate.awalMonth, -1);
            result = {
                date: Helper.getDayNumbersOfAwalMonth(previousMonth.year, previousMonth.month) + newDays,
                awalMonth: previousMonth
            };
        }
        else {
            result = {
                date: newDays,
                awalMonth: currentDate.awalMonth
            };
        }

        return result;
    }

    static addAwalMonths(currentMonth: AwalMonth, addedMonths: number) {
        let newMonth = currentMonth.month + addedMonths;
        let quotient = Math.floor(newMonth / 12);
        let remain = Helper.getMod(newMonth, 12);

        let result: AwalMonth = {
            month: remain,
            year: Helper.addAwalYears(currentMonth.year, quotient)
        }

        return result;
    }

    static addAwalYears(currentYear: AwalYear, addedYears: number) {
        let newIkasSarak = currentYear.ikasSarak + addedYears;
        let remain = Helper.getMod(newIkasSarak, 8);
        let newYearNumber = (currentYear.yearNumber ?? 0) + addedYears;

        let result: AwalYear = {
            ikasSarak: remain,
            yearNumber: newYearNumber
        }

        return result;
    }

    static getDayNumbersOfAwalMonth(year: AwalYear, month: AwalMonthEnum) {
        let numberOfDay = 0;

        if (month === AwalMonthEnum.Muharam || month === AwalMonthEnum.Rabiulawal || month === AwalMonthEnum.Jamadilawal ||
            month === AwalMonthEnum.Rejab || month === AwalMonthEnum.Ramadan || month === AwalMonthEnum.Julkaejah) {
            // Tháng lẻ: (30 ngày), gồm: 1,3,5,7,9,11.
            numberOfDay = 30;
        } else if (month === AwalMonthEnum.Syafar || month === AwalMonthEnum.Rabiulakhir || month === AwalMonthEnum.Jamadilakhir ||
            month === AwalMonthEnum.Sykban || month === AwalMonthEnum.Syawal) {
            // Tháng chẳn: (29 ngày), gồm: 2,4,6,8,10. 
            numberOfDay = 29;
        } else {
            // Riêng tháng 12: 
            if (year.ikasSarak === IkasSarakEnum.Hak || year.ikasSarak === IkasSarakEnum.Dal || year.ikasSarak === IkasSarakEnum.JimLuic) {
                // năm nhuận (thun "Nâh": Hak, Dal, Jim luic): 30 ngày
                numberOfDay = 30;
            } else {
                // năm thường (thun "Wak"): 29 ngày
                numberOfDay = 29;
            }
        }

        return numberOfDay;
    }

    static getStartDayByAwalMonth(year: AwalYear, month: AwalMonthEnum) {
        let yearName = IkasSarakEnum[year.ikasSarak];
        let yearItem = sakawiTakaiCiimConfig.filter(x => x.ArabYear === yearName)[0];
        let result = '';

        switch (month) {
            case 0:
                result = yearItem['Month_01'];
                break;
            case 1:
                result = yearItem['Month_02'];
                break;
            case 2:
                result = yearItem['Month_03'];
                break;
            case 3:
                result = yearItem['Month_04'];
                break;
            case 4:
                result = yearItem['Month_05'];
                break;
            case 5:
                result = yearItem['Month_06'];
                break;
            case 6:
                result = yearItem['Month_07'];
                break;
            case 7:
                result = yearItem['Month_08'];
                break;
            case 8:
                result = yearItem['Month_09'];
                break;
            case 9:
                result = yearItem['Month_10'];
                break;
            case 10:
                result = yearItem['Month_11'];
                break;
            case 11:
                result = yearItem['Month_12'];
                break;
            default:
                break;
        }

        return result;
    }

    static getAwalDateByGregoryDate(date: Date) {
        let differenceInTime = date.getTime() - firstDateOfSakawiAwal_Lieh_1407.getTime();
        let differenceInDays = differenceInTime / (1000 * 3600 * 24);
        let remain = Helper.getMod(differenceInDays, totalDaysOf8AwalYearCycle);
        let quotient = Math.floor(Math.abs(differenceInDays) / totalDaysOf8AwalYearCycle);

        let awalYearNumber = yearNumberOfSakawiAwal_Lieh_1407;
        let awalYear = IkasSarakEnum.Liéh;
        let awalMonth = AwalMonthEnum.Muharam;
        let awalDate = 0;
        let totalYears = 0;
        for (let i = 0; i < awalYearArray.length; i++) {
            totalYears += awalYearArray[i].numberOfDays;
            if (remain < totalYears) {
                awalYear = awalYearArray[i].name;

                if (differenceInDays > 0) {
                    awalYearNumber += (quotient * 8) + i;
                } else {
                    awalYearNumber -= (quotient * 8) + i;
                }

                let numberDaysOfCurrentYear = remain - (totalYears - awalYearArray[i].numberOfDays);
                let totalMonths = 0;

                for (let j = 0; j < awalMonthArray.length; j++) {
                    totalMonths += awalMonthArray[j].numberOfDays;
                    if (numberDaysOfCurrentYear < totalMonths) {
                        awalMonth = awalMonthArray[j].name;
                        let numberDaysOfCurrentMonth = numberDaysOfCurrentYear - (totalMonths - awalMonthArray[j].numberOfDays);
                        awalDate = numberDaysOfCurrentMonth + 1;
                        break;
                    }
                }
                break;
            }
        }

        let resultDate: AwalDate = {
            date: awalDate,
            awalMonth: { month: awalMonth, year: { ikasSarak: awalYear, yearNumber: awalYearNumber } }
        }

        return resultDate;
    }
    //#endregion

    //#region Ahier
    static addAhierDays(maxtrixCalendar: MatrixCalendarType[], currentDate: AhierDate, addedDays: number) {
        const numberOfDays = Helper.getActualDayNumbersOfAhierMonth(maxtrixCalendar, currentDate.ahierMonth);
        const newDays = currentDate.date + addedDays;

        let result: AhierDate = {
            date: 1,
            ahierMonth: {
                month: AhierMonthEnum.BilanSa,
                year: { nasak: NasakEnum.Takuh, ikasSarak: IkasSarakEnum.Liéh, yearNumber: 0 }
            }
        };

        if (newDays > numberOfDays) {
            const nextMonth = Helper.addAhierMonths(currentDate.ahierMonth, 1);
            result = {
                date: newDays - numberOfDays,
                ahierMonth: nextMonth
            };
        } else if (newDays <= 0) {
            const previousMonth = Helper.addAhierMonths(currentDate.ahierMonth, -1);
            const dayNumberOfPreviousMonth = Helper.getActualDayNumbersOfAhierMonth(maxtrixCalendar, previousMonth);
            result = {
                date: dayNumberOfPreviousMonth + newDays,
                ahierMonth: previousMonth
            };
        }
        else {
            result = {
                date: newDays,
                ahierMonth: currentDate.ahierMonth
            };
        }

        return result;
    }

    static addAhierMonths(currentMonth: AhierMonth, addedMonths: number) {
        const numberOfMonths = Helper.getMonthNumbersOfAhierYear(currentMonth.year);
        const newMonth = currentMonth.month + addedMonths;
        let result: AhierMonth = {
            month: currentMonth.month,
            year: currentMonth.year
        }

        if (newMonth > numberOfMonths - 1) {
            const nextYear = Helper.addAhierYears(currentMonth.year, 1);
            result = {
                month: newMonth - numberOfMonths,
                year: nextYear
            }
        } else if (newMonth < 0) {
            const previousYear = Helper.addAhierYears(currentMonth.year, -1);
            const numberOfMonthsOfPreviousYear = Helper.getMonthNumbersOfAhierYear(previousYear);

            result = {
                month: numberOfMonthsOfPreviousYear + newMonth,
                year: previousYear
            }
        } else {
            result = {
                month: newMonth,
                year: currentMonth.year
            }
        }

        return result;
    }

    static addAhierYears(currentYear: AhierYear, addedYears: number) {
        let newNasak = (currentYear.nasak + addedYears) % 12;
        let remainNasak = Helper.getMod(newNasak, 12);

        let newIkasSarak = (currentYear.ikasSarak + addedYears) % 8;
        let remainIkasSarak = Helper.getMod(newIkasSarak, 8);

        let newYearNumber = (currentYear.yearNumber ?? 0) + addedYears;

        let result: AhierYear = {
            nasak: remainNasak,
            ikasSarak: remainIkasSarak,
            yearNumber: newYearNumber
        }

        return result;
    }

    static getExpectedDayNumbersOfAhierMonth(year: AhierYear, month: AhierMonthEnum) {
        let numberOfDay = 0;
        if (month === AhierMonthEnum.BilanSa || month === AhierMonthEnum.BilanKlau || month === AhierMonthEnum.BilanLima ||
            month === AhierMonthEnum.BilanTajuh || month === AhierMonthEnum.BilanSalipan || month === AhierMonthEnum.BilanPuis) {
            // Tháng lẻ - "bilan tapak": (30 ngày), gồm: 1,3,5,7,9,11.
            numberOfDay = 30;
        } else if (month === AhierMonthEnum.BilanDua || month === AhierMonthEnum.BilanPak || month === AhierMonthEnum.BilanNem ||
            month === AhierMonthEnum.BilanDalipan || month === AhierMonthEnum.BilanSapluh) {
            // Tháng chẳn - "bilan u" : (29 ngày), gồm: 2,4,6,8,10. 
            numberOfDay = 29;
        } else if (month === AhierMonthEnum.BilanMak) {
            const isEndOfLongLeapPeriod144Years = Helper.checkIsEndOfLongLeapPeriod144Years(year);
            if (isEndOfLongLeapPeriod144Years) {
                numberOfDay = 29;
            } else {
                // Tháng 12:  
                if (year.ikasSarak === IkasSarakEnum.Hak || year.ikasSarak === IkasSarakEnum.Dal || year.ikasSarak === IkasSarakEnum.JimLuic) {
                    // năm nhuận (thun "Nâh": Hak, Dal, Jim luic): 30 ngày
                    numberOfDay = 30;
                } else {
                    // năm thường (thun "Wak"): 29 ngày
                    numberOfDay = 29;
                }
            }
        } else if (month === AhierMonthEnum.BilanBhang) {
            if (year.ikasSarak === IkasSarakEnum.Hak || year.ikasSarak === IkasSarakEnum.Dal || year.ikasSarak === IkasSarakEnum.JimLuic) {
                // năm nhuận (thun "Nâh": Hak, Dal, Jim luic): 29 ngày
                numberOfDay = 29;
            }
        }

        return numberOfDay;
    }

    static getActualDayNumbersOfAhierMonth(maxtrixCalendar: MatrixCalendarType[], ahierMonth: AhierMonth) {
        const index = maxtrixCalendar.findIndex(x => JSON.stringify(x.ahierMonth) === JSON.stringify(ahierMonth));
        if (index !== -1) {
            return maxtrixCalendar[index].dayNumbersOfAhierMonth;
        } else {
            return 0;
        }
    }

    static getMonthNumbersOfAhierYear(year: AhierYear) {
        const isEndOfLongLeapPeriod144Years = Helper.checkIsEndOfLongLeapPeriod144Years(year);
        if (isEndOfLongLeapPeriod144Years) {
            return 12;
        } else if (year.ikasSarak === IkasSarakEnum.Hak
            || year.ikasSarak === IkasSarakEnum.Dal
            || year.ikasSarak === IkasSarakEnum.JimLuic) {
            return 13;
        }
        return 12;
    }
    //#endregion

    //#region Gregory
    static addGregoryDays(date: Date, numberOfDays: number) {
        let newDt = new Date(date);
        newDt.setDate(newDt.getDate() + numberOfDays);
        return newDt;
    }
    //#endregion

    static buildMatrixCalendar(toYearAhier: number, areaType: AreaType) {
        let matrixCalendar: MatrixCalendarType[] = [];
        let fullCalendar: FullCalendarType[] = [];

        // Choose thun 1988 as a root
        const startAhierYear: AhierYear = {
            nasak: NasakEnum.InâGirai,
            ikasSarak: IkasSarakEnum.Liéh,
            yearNumber: 1988
        }

        // TO-TEST
        // const startAhierYear: AhierYear = {
        //     nasak: NasakEnum.Rimaong,
        //     ikasSarak: IkasSarakEnum.Jim,
        //     yearNumber: 2022
        // }

        const numberOfAhierYear = toYearAhier - (startAhierYear.yearNumber ?? 0);
        let newGregoryDate = firstDateOfSakawiAhier_InaGirai_Lieh_1988;
        //let newGregoryDate = new Date(2022, 3, 30);//16/04/1988

        for (let y = 0; y < numberOfAhierYear; y++) {
            const ahierYear = Helper.addAhierYears(startAhierYear, y);
            const matrixPerYear = Helper.renderMatrixPerYear(ahierYear, newGregoryDate);
            const validMatrix = Helper.applyGuenGuecRules(matrixPerYear, areaType);
            matrixCalendar.push(...validMatrix);

            const calendarDetails = Helper.renderCalendarDetails(validMatrix);
            fullCalendar.push(...calendarDetails);

            const lastMonthItem = validMatrix[validMatrix.length - 1];
            newGregoryDate = Helper.addGregoryDays(lastMonthItem.dateOfGregoryCalendar, lastMonthItem.dayNumbersOfAhierMonth);
        }

        return {
            matrixCalendar,
            fullCalendar
        };
    }

    static renderMatrixPerYear(ahierYear: AhierYear, firstGregoryDate: Date) {
        let matrixPerYear: MatrixCalendarType[] = [];
        let newGregoryDate = firstGregoryDate;
        let addedGregoryDays = 0;
        const numberOfAhierMonth = Helper.getMonthNumbersOfAhierYear(ahierYear);

        for (let m = 0; m < numberOfAhierMonth; m++) {
            const ahierMonth: AhierMonth = { month: m, year: ahierYear };
            const dayNumbersOfAhierMonth = Helper.getExpectedDayNumbersOfAhierMonth(ahierYear, m);
            const firstDayOfAhierMonth = newGregoryDate.getDay();

            const awalDate = Helper.getAwalDateByGregoryDate(newGregoryDate);
            const awalMonth = Helper.addAwalMonths(awalDate.awalMonth, 1);
            const dayNumbersOfAwalMonth = Helper.getDayNumbersOfAwalMonth(awalMonth.year, awalMonth.month);
            const firstDayOfAwalMonth = Number.parseInt(Helper.getStartDayByAwalMonth(awalMonth.year, awalMonth.month)) - 1; // Sunday is 0 

            let ahierMonthItem: MatrixCalendarType = {
                ahierMonth: ahierMonth,
                dayNumbersOfAhierMonth: dayNumbersOfAhierMonth,
                hasGuen: false,
                typeOfGuen: GuenTypeEnum.None,
                hasGuec: false,
                typeOfGuec: GuecTypeEnum.None,
                firstDayOfAhierMonth: firstDayOfAhierMonth,
                dateOfGregoryCalendar: newGregoryDate,
                awalMonth: awalMonth,
                dayNumbersOfAwalMonth: dayNumbersOfAwalMonth,
                firstDayOfAwalMonth: firstDayOfAwalMonth
            }

            matrixPerYear.push(ahierMonthItem);

            addedGregoryDays += dayNumbersOfAhierMonth;
            newGregoryDate = Helper.addGregoryDays(firstGregoryDate, addedGregoryDays);
        }

        return matrixPerYear;
    }

    static applyGuenGuecRules(matrixPerYear: MatrixCalendarType[], areaType: AreaType) {
        let monthGuen = -1;
        let monthGuec = -1;
        let guecTypeInNextYear = GuecTypeEnum.None;
        let hasGuenRuleInNextYear = false;

        // Check validations and fix in current year
        for (let index = 0; index < matrixPerYear.length; index++) {
            const element = matrixPerYear[index];

            // Guen
            if (monthGuen === -1 && Helper.checkIsGuenToAddDay(element.firstDayOfAhierMonth, element.firstDayOfAwalMonth)) {
                if (areaType === 'BinhThuan' || (areaType === 'NinhThuan' && index >= 11)) {
                    monthGuen = index;
                    matrixPerYear[monthGuen - 1].dayNumbersOfAhierMonth += 1;
                    matrixPerYear[monthGuen - 1].hasGuen = true;
                    matrixPerYear[monthGuen - 1].typeOfGuen = GuenTypeEnum.GuenByNormalRule;
                }
            }

            if (monthGuen !== -1 && index >= monthGuen) {
                const newDate = Helper.addGregoryDays(matrixPerYear[index].dateOfGregoryCalendar, 1);
                matrixPerYear[index].dateOfGregoryCalendar = newDate;
                matrixPerYear[index].firstDayOfAhierMonth = newDate.getDay();
            }

            // Guec
            if (monthGuec === -1 && Helper.checkIsGuecToMinusDay(element.firstDayOfAhierMonth, element.firstDayOfAwalMonth)) {
                monthGuec = index;
                matrixPerYear[monthGuec - 1].dayNumbersOfAhierMonth -= 1;
                matrixPerYear[monthGuec - 1].hasGuec = true;
                matrixPerYear[monthGuec - 1].typeOfGuec = GuecTypeEnum.GuecByNormalRule;
            }

            if (monthGuec !== -1 && index >= monthGuec) {
                const newDate = Helper.addGregoryDays(matrixPerYear[index].dateOfGregoryCalendar, -1);
                matrixPerYear[index].dateOfGregoryCalendar = newDate;
                matrixPerYear[index].firstDayOfAhierMonth = newDate.getDay();
            }
        }

        // Check validations in next year to fix current year
        const lastMonthOfCurrentYear = matrixPerYear[matrixPerYear.length - 1];
        const firstGregoryDateOfNextYear = Helper.addGregoryDays(lastMonthOfCurrentYear.dateOfGregoryCalendar, lastMonthOfCurrentYear.dayNumbersOfAhierMonth);
        guecTypeInNextYear = Helper.checkHasGuecRuleInNextYear(lastMonthOfCurrentYear.ahierMonth.year, firstGregoryDateOfNextYear);
        hasGuenRuleInNextYear = Helper.checkHasGuenRuleInNextYear(lastMonthOfCurrentYear.ahierMonth.year, firstGregoryDateOfNextYear);

        if (guecTypeInNextYear !== GuecTypeEnum.None) {
            // Bilan Mak (12)
            matrixPerYear[11].dayNumbersOfAhierMonth -= 1;
            matrixPerYear[11].hasGuec = true;
            matrixPerYear[11].typeOfGuec = guecTypeInNextYear;

            // Bilan Bhang (13)
            if (matrixPerYear.length > 12) {
                const newDate = Helper.addGregoryDays(matrixPerYear[12].dateOfGregoryCalendar, -1);
                matrixPerYear[12].dateOfGregoryCalendar = newDate;
                matrixPerYear[12].firstDayOfAhierMonth = newDate.getDay();
            }
        } else if (hasGuenRuleInNextYear) {
            // Bilan (12) or (13)
            if (Helper.getAhierAwalDaysGap(lastMonthOfCurrentYear.firstDayOfAhierMonth, lastMonthOfCurrentYear.firstDayOfAwalMonth) === 2) {
                lastMonthOfCurrentYear.dayNumbersOfAhierMonth += 1;
                lastMonthOfCurrentYear.hasGuen = true;
                lastMonthOfCurrentYear.typeOfGuen = GuenTypeEnum.GuenByNormalRule;
            }
        }

        return [...matrixPerYear];
    }

    static renderCalendarDetails(matrixPerYear: MatrixCalendarType[]) {
        let fullCalendar: FullCalendarType[] = [];

        for (let index = 0; index < matrixPerYear.length; index++) {
            const element = matrixPerYear[index];
            const firstDateOfAhierMonth: AhierDate = { date: 1, ahierMonth: element.ahierMonth };
            const firstDateOfAwalMonth: AwalDate = { date: 1, awalMonth: element.awalMonth };
            const firstDateOfGregoryMonth = element.dateOfGregoryCalendar;
            const daysGap = Helper.getAhierAwalDaysGap(element.firstDayOfAhierMonth, element.firstDayOfAwalMonth);

            for (let days = 0; days < element.dayNumbersOfAhierMonth; days++) {
                const addedAhierDate = Helper.addAhierDays(matrixPerYear, firstDateOfAhierMonth, days);
                const dateAhier: AhierDate = {
                    date: addedAhierDate.date,
                    ahierMonth: addedAhierDate.ahierMonth
                }

                const addedAwalDate = Helper.addAwalDays(firstDateOfAwalMonth, days - daysGap);
                const dateAwal: AwalDate = {
                    date: addedAwalDate.date,
                    awalMonth: addedAwalDate.awalMonth
                }

                const dateGregory = Helper.addGregoryDays(firstDateOfGregoryMonth, days);

                let monthDetailsCalendar: FullCalendarType = {
                    dateAhier: dateAhier,
                    dateAwal: dateAwal,
                    dateGregory: dateGregory,
                    typeOfGuen: days === element.dayNumbersOfAhierMonth - 1 ? element.typeOfGuen : undefined,
                    typeOfGuec: days === element.dayNumbersOfAhierMonth - 1 ? element.typeOfGuec : undefined,
                    hasGuen: days === element.dayNumbersOfAhierMonth - 1 ? element.hasGuen : undefined,
                    hasGuec: days === element.dayNumbersOfAhierMonth - 1 ? element.hasGuec : undefined
                }

                fullCalendar.push(monthDetailsCalendar);
            }
        }

        return fullCalendar;
    }

    static checkIsGuenToAddDay(firstDayOfAhierMonth: number, firstDayOfAwalMonth: number) {
        const daysGap = Helper.getAhierAwalDaysGap(firstDayOfAhierMonth, firstDayOfAwalMonth);
        return daysGap > 2;
    }

    static checkIsGuecToMinusDay(firstDayOfAhierMonth: number, firstDayOfAwalMonth: number) {
        return firstDayOfAhierMonth === firstDayOfAwalMonth;
    }

    static checkHasGuecRuleInNextYear(currentYear: AhierYear, firstGregoryDateNextYear: Date) {
        let result = GuecTypeEnum.None;

        const nextAhierYear = Helper.addAhierYears(currentYear, 1);
        const matrixNextYear = Helper.renderMatrixPerYear(nextAhierYear, firstGregoryDateNextYear);
        const firstMonth = matrixNextYear[0];
        const isKateRamawanConflict = matrixNextYear.some(x =>
            x.ahierMonth.month === AhierMonthEnum.BilanTajuh &&
            x.awalMonth.month === AwalMonthEnum.Ramadan &&
            Helper.getAhierAwalDaysGap(x.firstDayOfAhierMonth, x.firstDayOfAwalMonth) === 1);
        const hasNormalGuec = matrixNextYear.some(item => Helper.checkIsGuecToMinusDay(item.firstDayOfAhierMonth, item.firstDayOfAwalMonth));

        if (hasNormalGuec) {
            result = GuecTypeEnum.GuecByNormalRule;
        } else if (nextAhierYear.ikasSarak === IkasSarakEnum.Liéh) {
            if (firstMonth.firstDayOfAhierMonth === 4 && firstMonth.firstDayOfAwalMonth === 5) {
                result = GuecTypeEnum.GuecByHareiButSukRule;
            } else if (isKateRamawanConflict) {
                result = GuecTypeEnum.GuecByKateRamawanRule;
            }
        } else if (firstMonth.firstDayOfAhierMonth === 5 && firstMonth.firstDayOfAwalMonth === 6) {
            result = GuecTypeEnum.GuecByRijaNagarRule;
        }

        return result;
    }

    static checkHasGuenRuleInNextYear(currentYear: AhierYear, firstGregoryDateNextYear: Date) {
        const nextAhierYear = Helper.addAhierYears(currentYear, 1);
        const matrixNextYear = Helper.renderMatrixPerYear(nextAhierYear, firstGregoryDateNextYear);

        return matrixNextYear.some(item => Helper.checkIsGuenToAddDay(item.firstDayOfAhierMonth, item.firstDayOfAwalMonth));
    }

    static checkIsEndOfLongLeapPeriod144Years(currentYear: AhierYear) {
        return currentYear.yearNumber !== 1995 && ((currentYear.yearNumber + 1) - 1996) % 144 === 0;
    }

    static getMod(n: number, m: number) {
        const remain = n % m;
        return Math.floor(remain >= 0 ? remain : remain + m);
    };

    static getAhierAwalDaysGap(firstDayOfAhierMonth: number, firstDayOfAwalMonth: number) {
        let daysGap = 0;

        if (firstDayOfAhierMonth < firstDayOfAwalMonth) {
            daysGap = firstDayOfAwalMonth - firstDayOfAhierMonth;
        } else if (firstDayOfAhierMonth > firstDayOfAwalMonth) {
            daysGap = firstDayOfAwalMonth + (6 - firstDayOfAhierMonth) + 1; // Sunday is 0
        }

        return daysGap;
    }

    static convertToChamDigitUnicode(latinNumber: number, showLatinNumer: boolean = false) {
        if (showLatinNumer) {
            return latinNumber;
        }

        const ChamDigitArr = ['꩐', '꩑', '꩒', '꩓', '꩔', '꩕', '꩖', '꩗', '꩘', '꩙'];
        const EnglishDigits = '0123456789';
        const reg = new RegExp('[' + EnglishDigits + ']', 'g');

        return latinNumber.toString().replace(reg, function (c) {
            return ChamDigitArr[EnglishDigits.indexOf(c)]
        });
    }

    static getNextEvents(fullCalendar: FullCalendarType[]) {
        let result: CountDownBarProps[] = [];
        let addedAkaokThun = false;
        let addedAwalNewYear = false;
        let addedRijaNagar = false;
        let addedVietnameseLunarNewYear = false;
        //let addedKateHamuTanran = false;
        let addedKate = false;
        let addedRamawan = false;
        // let addedMukTrun = false;
        // let addedOngTrun = false;
        // let addedTalaihRamawan = false;
        // let addedIkakWaha = false;
        // let addedTalaihWaha = false;
        // let addedYuerYang = false;

        const today = getToday();
        const cutoffDate = Helper.addGregoryDays(today, 390);

        for (const item of fullCalendar) {
            if (item.dateGregory < today) {
                continue;
            }

            let eventGregoryDate = item.dateGregory;
            if (eventGregoryDate > cutoffDate) {
                break;
            }

            if (!addedVietnameseLunarNewYear && Helper.isVietnameseLunarNewYear(eventGregoryDate)) {
                result.push({ eventType: 'VietnameseLunarNewYear', eventDate: eventGregoryDate });
                addedVietnameseLunarNewYear = true;
            }

            if (!addedAkaokThun && item.dateAhier.ahierMonth.month === 0 && item.dateAhier.date === 1) {
                result.push({ eventType: 'AkaokThun', eventDate: eventGregoryDate });
                addedAkaokThun = true;
            }

            if (!addedAwalNewYear && item.dateAwal.awalMonth.month === AwalMonthEnum.Muharam && item.dateAwal.date === 1) {
                result.push({ eventType: 'AwalNewYear', eventDate: eventGregoryDate });
                addedAwalNewYear = true;
            }

            // TODO
            if (!addedRijaNagar && item.dateAhier.ahierMonth.month === 0 && eventGregoryDate.getDay() === 4) {
                if (item.dateAwal.awalMonth.month === AwalMonthEnum.Ramadan) {
                    // closet Thurday and after Muk Trun day
                    if (item.dateAwal.date >= 16 && item.dateAhier.date <= 22) {
                        result.push({ eventType: 'RijaNagar', eventDate: eventGregoryDate });
                        addedRijaNagar = true;
                    }

                } else {
                    if (item.dateAhier.date >= 1 && item.dateAhier.date <= 7) {
                        result.push({ eventType: 'RijaNagar', eventDate: eventGregoryDate });
                        addedRijaNagar = true;
                    }
                }
            }

            /*if (!addedKateHamuTanran && item.dateAhier.ahierMonth.month === 5 && item.dateAhier.date === 29) {
                result.push({ eventType: 'Katé palei Hamu Tanran', eventDate: eventGregoryDate });
                addedKateHamuTanran = true;
            }*/

            if (!addedKate && item.dateAhier.ahierMonth.month === 6 && item.dateAhier.date === 1) {
                result.push({ eventType: 'KateAngaokBimong', eventDate: eventGregoryDate });
                addedKate = true;
            }

            /*if (item.dayNumbersOfCurrentAhierMonth === 30) {
                if (item.dateAhier.ahierMonth.month === 8 && item.dateAhier.date === 16) {
                    result.push('Ca-mbur');
                }
            } else {
                if (item.dateAhier.ahierMonth.month === 8 && item.dateAhier.date === 15) {
                    result.push('Ca-mbur');
                }
            }*/

            if (!addedRamawan && item.dateAwal.awalMonth.month === 8 && item.dateAwal.date === 1) {
                result.push({ eventType: 'TamaRicaowRamawan', eventDate: eventGregoryDate });
                addedRamawan = true;
            }

            /*if (!addedMukTrun && item.dateAwal.awalMonth.month === 8 && item.dateAwal.date === 16) {
                result.push({ eventType: 'Muk trun', eventDate: eventGregoryDate });
                addedMukTrun = true;
            }

            if (!addedOngTrun && item.dateAwal.awalMonth.month === 8 && item.dateAwal.date === 21) {
                result.push({ eventType: 'Ong trun', eventDate: eventGregoryDate });
                addedOngTrun = true;
            }

            if (!addedTalaihRamawan && item.dateAwal.awalMonth.month === 9 && item.dateAwal.date === 2) {
                result.push({ eventType: 'Talaih aek Ramâwan', eventDate: eventGregoryDate });
                addedTalaihRamawan = true;
            }

            if (!addedIkakWaha && item.dateAwal.awalMonth.month === 11 && item.dateAwal.date === 1) {
                result.push({ eventType: 'Ikak Waha', eventDate: eventGregoryDate });
                addedIkakWaha = true;
            }

            if (!addedTalaihWaha && item.dateAwal.awalMonth.month === 11 && item.dateAwal.date === 11) {
                result.push({ eventType: 'Talaih Waha', eventDate: eventGregoryDate });
                addedTalaihWaha = true;
            }

            if (!addedYuerYang && item.dateAhier.ahierMonth.month === 3 && eventGregoryDate.getDay() === 0 && item.dateAhier.date < 7) {
                result.push({ eventType: 'Yuer Yang', eventDate: eventGregoryDate });
                addedYuerYang = true;
            }*/

            // Break loop: just look up in a year later (30 days x 13 months)
            if (eventGregoryDate > Helper.addGregoryDays(getToday(), 390)) {
                break;
            }
        }

        return result;
    }

    static getEventsInAhierYear(maxtrixCalendar: MatrixCalendarType[], fullCalendar: FullCalendarType[]) {
        let result: CountDownBarProps[] = [];
        //let addedRijaNagar = false;

        fullCalendar.forEach(function (item, index) {
            let eventGregoryDate = item.dateGregory;

            if (Helper.isVietnameseLunarNewYear(eventGregoryDate)) {
                result.push({ eventType: 'VietnameseLunarNewYear', sakawiType: 'sakawiGregory', eventDate: eventGregoryDate });
            }

            if (item.dateAhier.ahierMonth.month === 0 && item.dateAhier.date === 1) {
                result.push({ eventType: 'AkaokThun', sakawiType: 'sakawiAhier', eventDate: eventGregoryDate });
            }

            if (item.dateAwal.awalMonth.month === AwalMonthEnum.Muharam && item.dateAwal.date === 1) {
                result.push({ eventType: 'AwalNewYear', sakawiType: 'sakawiAwal', eventDate: eventGregoryDate });
            }

            //TODO
            if (item.dateAhier.ahierMonth.month === 0 && eventGregoryDate.getDay() === 4) {
                if (item.dateAwal.awalMonth.month === AwalMonthEnum.Ramadan) {
                    // closet Thurday and after Muk Trun day
                    if (item.dateAwal.date >= 16 && item.dateAhier.date <= 22) {
                        result.push({ eventType: 'RijaNagar', eventDate: eventGregoryDate });
                    }
                } else {
                    if (item.dateAhier.date >= 1 && item.dateAhier.date <= 7) {
                        result.push({ eventType: 'RijaNagar', sakawiType: 'sakawiAhier', eventDate: eventGregoryDate });
                    }
                }
            }

            if (item.dateAhier.ahierMonth.month === 5 && item.dateAhier.date === 29) {
                result.push({ eventType: 'KatePaleiHamuTanran', sakawiType: 'sakawiAhier', eventDate: eventGregoryDate });
            }

            if (item.dateAhier.ahierMonth.month === 6 && item.dateAhier.date === 1) {
                result.push({ eventType: 'KateAngaokBimong', sakawiType: 'sakawiAhier', eventDate: eventGregoryDate });
            }

            const dayNumbersOfCurrentAhierMonth = Helper.getActualDayNumbersOfAhierMonth(maxtrixCalendar, item.dateAhier.ahierMonth);
            if (dayNumbersOfCurrentAhierMonth === 30) {
                if (item.dateAhier.ahierMonth.month === 8 && item.dateAhier.date === 16) {
                    result.push({ eventType: 'CaMbur', sakawiType: 'sakawiAhier', eventDate: eventGregoryDate });
                }
            } else {
                if (item.dateAhier.ahierMonth.month === 8 && item.dateAhier.date === 15) {
                    result.push({ eventType: 'CaMbur', sakawiType: 'sakawiAhier', eventDate: eventGregoryDate });
                }
            }

            if (item.dateAwal.awalMonth.month === 8 && item.dateAwal.date === 1) {
                result.push({ eventType: 'TamaRicaowRamawan', sakawiType: 'sakawiAwal', eventDate: eventGregoryDate });
            }

            if (item.dateAwal.awalMonth.month === 8 && item.dateAwal.date === 16) {
                result.push({ eventType: 'MukTrun', sakawiType: 'sakawiAwal', eventDate: eventGregoryDate });
            }

            if (item.dateAwal.awalMonth.month === 8 && item.dateAwal.date === 21) {
                result.push({ eventType: 'OngTrun', sakawiType: 'sakawiAwal', eventDate: eventGregoryDate });
            }

            if (item.dateAwal.awalMonth.month === 9 && item.dateAwal.date === 2) {
                result.push({ eventType: 'TalaihAekRamawan', sakawiType: 'sakawiAwal', eventDate: eventGregoryDate });
            }

            if (item.dateAwal.awalMonth.month === 11 && item.dateAwal.date === 1) {
                result.push({ eventType: 'IkakWaha', sakawiType: 'sakawiAwal', eventDate: eventGregoryDate });
            }

            if (item.dateAwal.awalMonth.month === 11 && item.dateAwal.date === 11) {
                result.push({ eventType: 'TalaihWaha', sakawiType: 'sakawiAwal', eventDate: eventGregoryDate });
            }

            if (item.dateAhier.ahierMonth.month === 3 && eventGregoryDate.getDay() === 0 && item.dateAhier.date < 7) {
                result.push({ eventType: 'YuerYang', sakawiType: 'sakawiAhier', eventDate: eventGregoryDate });
            }
        });

        return result;
    }

    static displayDateString(date: Date) {
        let dd = date.getDate();
        let mm = date.getMonth() + 1;
        const yyyy = date.getFullYear();
        const ddStr = dd < 10 ? '0' + dd : dd;
        const mmStr = mm < 10 ? '0' + mm : mm;
        return ddStr + '/' + mmStr + '/' + yyyy;
    }

    static displayEventDay(eventType: EventType) {
        return chamEventDaysConfig.filter(x => x.eventType === eventType)[0];
    }
}
